import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { cartAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cartAPI.get();
      // Swell cart object has many fields (sub_total, shipment_total, discount_total, etc.)
      setCart({
        ...response,
        items: response?.items || [],
        total: response?.sub_total ?? response?.total ?? 0 // Use sub_total or total
      });
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, variantIdOrSize, quantity = 1, isVariant = false) => {
    try {
      // For products with variants, use variant_id. Otherwise use size option
      const itemData = isVariant
        ? {
            product_id: productId,
            variant_id: variantIdOrSize,
            quantity
          }
        : {
            product_id: productId,
            quantity,
            options: variantIdOrSize ? { size: variantIdOrSize } : undefined
          };

      await cartAPI.add(itemData);
      await fetchCart();
      toast.success('Added to cart!');
      setIsOpen(true);
      return true;
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error('Failed to add to cart');
      return false;
    }
  }, [fetchCart]);

  const updateQuantity = useCallback(async (itemId, quantity) => {
    try {
      await cartAPI.update(itemId, quantity);
      await fetchCart();
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  }, [fetchCart]);

  const removeFromCart = useCallback(async (itemId) => {
    try {
      await cartAPI.remove(itemId);
      await fetchCart();
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  }, [fetchCart]);

  const clearCart = useCallback(async () => {
    try {
      await cartAPI.clear();
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  }, []);

  const itemCount = useMemo(() => cart.items.reduce((sum, item) => sum + item.quantity, 0), [cart.items]);

  const value = useMemo(() => ({
    cart,
    loading,
    isOpen,
    setIsOpen,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart,
    itemCount
  }), [cart, loading, isOpen, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, itemCount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
